import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo, useState } from "react";
import type { Nullable, StateSetter } from "../types";

type SelectState = {
  isOpen: boolean;
  portalRef: null | HTMLElement;
  closeOnSelect: boolean;
};

const SelectStateContext = createContext<SelectState>({
  isOpen: false,
  portalRef: null,
  closeOnSelect: true,
});
const SelectOpenToggleContext = createContext<VoidFunction>(() => null);
const SelectOffContext = createContext<VoidFunction>(() => null);
const SelectPortalContext = createContext<StateSetter<Nullable<HTMLElement>>>(() => null);

export const SelectProvider = ({
  children,
  toggleOpen,
  isOpen,
  setOff,
  closeOnSelect,
}: PropsWithChildren<{
  isOpen: boolean;
  closeOnSelect: boolean;
  toggleOpen: VoidFunction;
  setOff: VoidFunction;
}>) => {
  const [portalRef, setPortalRef] = useState<Nullable<HTMLElement>>(null);

  const portalState = useMemo<SelectState>(
    () => ({
      isOpen,
      portalRef,
      closeOnSelect,
    }),
    [isOpen, portalRef, closeOnSelect],
  );

  return (
    <SelectOffContext.Provider value={setOff}>
      <SelectOpenToggleContext.Provider value={toggleOpen}>
        <SelectStateContext.Provider value={portalState}>
          <SelectPortalContext.Provider value={setPortalRef}>
            {children}
          </SelectPortalContext.Provider>
        </SelectStateContext.Provider>
      </SelectOpenToggleContext.Provider>
    </SelectOffContext.Provider>
  );
};

export const useSelectState = () => useContext(SelectStateContext);
export const useSelectToggle = () => useContext(SelectOpenToggleContext);
export const useSelectOff = () => useContext(SelectOffContext);
export const useSelectPortal = () => useContext(SelectPortalContext);
